"use client";
import { memo } from "react";
import Image from "next/image";
import logo from "@public/logo.svg";
import { useTranslations } from "next-intl";
import { RefreshCcw, WifiOff, Home, UserX } from "lucide-react";

/** Props for the ConnectionErrorOverlay component */
interface ConnectionErrorOverlayProps {
  error: string;
  isKicked?: boolean;
  onRetry: () => void;
  onHome: () => void;
}

/** ConnectionErrorOverlay Component */
const ConnectionErrorOverlay = memo(({ error, isKicked, onRetry, onHome }: ConnectionErrorOverlayProps) => {
  const t = useTranslations();

  return (
    <div className="fixed inset-0 z-1000 flex flex-col items-center justify-center bg-linear-to-br from-white via-slate-50 to-gray-100 text-gray-900 overflow-hidden">
      {/* Background Pattern - Subtle dots (darker for light theme) */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(#000000 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      ></div>

      {/* Header with Logo */}
      <div className="absolute top-0 left-0 w-full px-6 py-4 z-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Image src={logo} alt="Freiburger Meet" width={180} height={32} style={{ height: "auto" }} priority />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="relative z-10 flex flex-col items-center max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300">
        {/* Icon Circle with Pulse Effect */}
        <div className="relative mb-8">
          <div
            className={`absolute inset-0 rounded-full animate-ping opacity-75 ${isKicked ? "bg-blue-500/10" : "bg-error/10"}`}
          ></div>
          <div
            className={`relative flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-sm ring-1 ${isKicked ? "ring-blue-200" : "ring-error/20"}`}
          >
            {isKicked ? (
              <UserX className="w-10 h-10 text-primary" strokeWidth={1.5} />
            ) : (
              <WifiOff className="w-10 h-10 text-error" strokeWidth={1.5} />
            )}
          </div>
        </div>

        <h1 className="text-3xl font-medium tracking-tight mb-3 text-gray-900">
          {isKicked ? t("errors.removedTitle") : t("errors.disconnectedTitle")}
        </h1>

        <p className="text-gray-500 text-lg mb-10 leading-relaxed font-light">
          {isKicked ? t("errors.removedDescription") : t("errors.connectionTrouble")}
          <br />
          <span
            className={`text-xs font-medium opacity-100 mt-3 block font-mono py-1.5 px-4 rounded-full border shadow-sm ${
              isKicked ? "bg-blue-50 text-primary border-blue-100" : "bg-error/5 text-error border-error/10"
            }`}
          >
            {isKicked ? t("errors.kicked") : error}
          </span>
        </p>

        {/* Action Buttons */}
        {!isKicked && (
          <div className="flex flex-col w-full gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <button
              onClick={onRetry}
              className="group relative flex items-center justify-center gap-2 px-8 py-3.5 bg-primary hover:bg-[#0088c7] text-white rounded-full font-medium transition-all duration-200 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 focus:ring-2 focus:ring-primary/50 outline-none"
            >
              <RefreshCcw className="w-4 h-4 transition-transform group-hover:rotate-180" />
              <span>{t("common.retry")}</span>
            </button>

            <button
              onClick={onHome}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-gray-50 text-gray-700 rounded-full font-medium border border-gray-200 hover:border-gray-300 transition-all duration-200 outline-none shadow-sm focus:ring-2 focus:ring-gray-200"
            >
              <Home className="w-4 h-4 text-gray-500" />
              <span>{t("meeting.returnHome")}</span>
            </button>
          </div>
        )}

        {/* Footer text */}
        {!isKicked && <p className="mt-12 text-sm text-gray-400">{t("errors.checkInternet")}</p>}
      </div>
    </div>
  );
});

ConnectionErrorOverlay.displayName = "ConnectionErrorOverlay";

export default ConnectionErrorOverlay;
